import React from "react";
import PropTypes from "prop-types";
import { Switch, Label, makeStyles } from "@fluentui/react-components";
import { useIntl } from "react-intl";

const useStyles = makeStyles({
  labelDisabled: {
    color: "var(--colorNeutralBackgroundInvertedDisabled)"
  }
});

const LabeledSwitch = ({ labelId, labelDefaultMessage, checked, onChange, id, disabled = false }) => {
  const intl = useIntl();
  const styles = useStyles();

  return (
    <>
      <Label htmlFor={id} className={disabled ? styles.labelDisabled : styles.label}>
        {intl.formatMessage({
          id: labelId,
          defaultMessage: labelDefaultMessage
        })}
      </Label>
      <Switch checked={checked || false} onChange={onChange} id={id} disabled={disabled} />
    </>
  );
};

LabeledSwitch.propTypes = {
  labelId: PropTypes.string.isRequired,
  labelDefaultMessage: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

/**
 * Disable Sound Effects Switch Component
 */
export function DisableSoundEffectsSwitch({ disableSoundEffects, onDisableSoundEffectsChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.disable-sound-effects"
      labelDefaultMessage="Disable Sound Effects"
      checked={disableSoundEffects}
      onChange={onDisableSoundEffectsChange}
      id="disable-sound-effects-switch"
    />
  );
}

DisableSoundEffectsSwitch.propTypes = {
  disableSoundEffects: PropTypes.bool.isRequired,
  onDisableSoundEffectsChange: PropTypes.func.isRequired
};

/**
 * Disable Mic Echo Cancellation Switch Component
 */
export function DisableMicEchoCancellationSwitch({ disableMicEchoCancellation, onDisableMicEchoCancellationChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.disable-mic-echo-cancellation"
      labelDefaultMessage="Disable Mic Echo Cancellation"
      checked={disableMicEchoCancellation}
      onChange={onDisableMicEchoCancellationChange}
      id="disable-mic-echo-cancellation-switch"
    />
  );
}

DisableMicEchoCancellationSwitch.propTypes = {
  disableMicEchoCancellation: PropTypes.bool.isRequired,
  onDisableMicEchoCancellationChange: PropTypes.func.isRequired
};

/**
 * Disable Mic Noise Suppression Switch Component
 */
export function DisableMicNoiseSuppressionSwitch({ disableMicNoiseSuppression, onDisableMicNoiseSuppressionChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.disable-mic-noise-suppression"
      labelDefaultMessage="Disable Mic Noise Suppression"
      checked={disableMicNoiseSuppression}
      onChange={onDisableMicNoiseSuppressionChange}
      id="disable-mic-noise-suppression-switch"
    />
  );
}

DisableMicNoiseSuppressionSwitch.propTypes = {
  disableMicNoiseSuppression: PropTypes.bool.isRequired,
  onDisableMicNoiseSuppressionChange: PropTypes.func.isRequired
};

/**
 * Disable Mic Auto Gain Control Switch Component
 */
export function DisableMicAutoGainControlSwitch({ disableMicAutoGainControl, onDisableMicAutoGainControlChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.disable-mic-auto-gain-control"
      labelDefaultMessage="Disable Mic Auto Gain Control"
      checked={disableMicAutoGainControl}
      onChange={onDisableMicAutoGainControlChange}
      id="disable-mic-auto-gain-control-switch"
    />
  );
}

DisableMicAutoGainControlSwitch.propTypes = {
  disableMicAutoGainControl: PropTypes.bool.isRequired,
  onDisableMicAutoGainControlChange: PropTypes.func.isRequired
};

/**
 * Enable Audio Clipping Switch Component
 */
export function EnableAudioClippingSwitch({ enableAudioClipping, onEnableAudioClippingChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.enable-audio-clipping"
      labelDefaultMessage="Enable Audio Clipping"
      checked={enableAudioClipping}
      onChange={onEnableAudioClippingChange}
      id="enable-audio-clipping-switch"
    />
  );
}

EnableAudioClippingSwitch.propTypes = {
  enableAudioClipping: PropTypes.bool.isRequired,
  onEnableAudioClippingChange: PropTypes.func.isRequired
};

/**
 * Show Audio Debug Panel Switch Component
 */
export function ShowAudioDebugPanelSwitch({ showAudioDebugPanel, onShowAudioDebugPanelChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.show-audio-debug-panel"
      labelDefaultMessage="Show Audio Debug Panel"
      checked={showAudioDebugPanel}
      onChange={onShowAudioDebugPanelChange}
      id="show-audio-debug-panel-switch"
    />
  );
}

ShowAudioDebugPanelSwitch.propTypes = {
  showAudioDebugPanel: PropTypes.bool.isRequired,
  onShowAudioDebugPanelChange: PropTypes.func.isRequired
};

/**
 * Disable Audio L/R Panning Switch Component
 */
export function DisableAudioPanningSwitch({ disableAudioPanning, onDisableAudioPanningChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.disable-audio-l-r-panning"
      labelDefaultMessage="Disable Audio L/R Panning"
      checked={disableAudioPanning}
      onChange={onDisableAudioPanningChange}
      id="disable-audio-l-r-panning-switch"
    />
  );
}

DisableAudioPanningSwitch.propTypes = {
  disableAudioPanning: PropTypes.bool.isRequired,
  onDisableAudioPanningChange: PropTypes.func.isRequired
};

/**
 * Enable Dynamic Shadows Switch Component
 */
export function EnableDynamicShadowsSwitch({ enableDynamicShadows, onEnableDynamicShadowsChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.enable-dynamic-shadows"
      labelDefaultMessage="Enable Real-time Shadows"
      checked={enableDynamicShadows}
      onChange={onEnableDynamicShadowsChange}
      id="enable-dynamic-shadows-switch"
    />
  );
}

EnableDynamicShadowsSwitch.propTypes = {
  enableDynamicShadows: PropTypes.bool.isRequired,
  onEnableDynamicShadowsChange: PropTypes.func.isRequired
};

/**
 * Disable Auto Pixel Ratio Switch Component
 */
export function DisableAutoPixelRatioSwitch({ disableAutoPixelRatio, onDisableAutoPixelRatioChange, disabled }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.disable-auto-pixel-ratio"
      labelDefaultMessage="Disable automatic pixel ratio adjustments"
      checked={disableAutoPixelRatio}
      onChange={onDisableAutoPixelRatioChange}
      id="disable-auto-pixel-ratio-switch"
      disabled={disabled}
    />
  );
}

DisableAutoPixelRatioSwitch.propTypes = {
  disableAutoPixelRatio: PropTypes.bool.isRequired,
  onDisableAutoPixelRatioChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

/**
 * Enable Bloom Effect Switch Component
 */
export function EnableBloomSwitch({ enableBloom, onEnableBloomChange, disabled }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.enable-fx-bloom"
      labelDefaultMessage="Enable Bloom Effect"
      checked={enableBloom}
      onChange={onEnableBloomChange}
      id="enable-bloom-switch"
      disabled={disabled}
    />
  );
}

EnableBloomSwitch.propTypes = {
  enableBloom: PropTypes.bool.isRequired,
  onEnableBloomChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

/**
 * Enable Post Processing Effects Switch Component
 */
export function EnablePostEffectsSwitch({ enablePostEffects, onEnablePostEffectsChange, disabled }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.enable-fx"
      labelDefaultMessage="Enable Post Processing Effects (experimental)"
      checked={enablePostEffects}
      onChange={onEnablePostEffectsChange}
      id="enable-post-effects-switch"
      disabled={disabled}
    />
  );
}

EnablePostEffectsSwitch.propTypes = {
  enablePostEffects: PropTypes.bool.isRequired,
  onEnablePostEffectsChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export function DisableMovementSwitch({ disableMovement, onDisableMovementChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.disable-movement"
      labelDefaultMessage="Disable movement"
      checked={disableMovement}
      onChange={onDisableMovementChange}
      id="disable-movement-switch"
    />
  );
}

DisableMovementSwitch.propTypes = {
  disableMovement: PropTypes.bool.isRequired,
  onDisableMovementChange: PropTypes.func.isRequired
};

export function DisableBackwardsMovementSwitch({ disableBackwardsMovement, onDisableBackwardsMovementChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.disable-backwards-movement"
      labelDefaultMessage="Disable backwards movement"
      checked={disableBackwardsMovement}
      onChange={onDisableBackwardsMovementChange}
      id="disable-backwards-movement-switch"
    />
  );
}

DisableBackwardsMovementSwitch.propTypes = {
  disableBackwardsMovement: PropTypes.bool.isRequired,
  onDisableBackwardsMovementChange: PropTypes.func.isRequired
};

export function DisableStrafingSwitch({ disableStrafing, onDisableStrafingChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.disable-strafing"
      labelDefaultMessage="Disable strafing"
      checked={disableStrafing}
      onChange={onDisableStrafingChange}
      id="disable-strafing-switch"
    />
  );
}

DisableStrafingSwitch.propTypes = {
  disableStrafing: PropTypes.bool.isRequired,
  onDisableStrafingChange: PropTypes.func.isRequired
};

export function DisableTeleporterSwitch({ disableTeleporter, onDisableTeleporterChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.disable-teleporter"
      labelDefaultMessage="Disable teleporter"
      checked={disableTeleporter}
      onChange={onDisableTeleporterChange}
      id="disable-teleporter-switch"
    />
  );
}

DisableTeleporterSwitch.propTypes = {
  disableTeleporter: PropTypes.bool.isRequired,
  onDisableTeleporterChange: PropTypes.func.isRequired
};

// enableOnScreenJoystickLeft: {
//   id: "preferences-screen.preference.enable-on-screen-joystick-left",
//   defaultMessage: "Enable left on-screen joystick for moving around"
// },
// enableOnScreenJoystickRight: {
//   id: "preferences-screen.preference.enable-on-screen-joystick-right",
//   defaultMessage: "Enable right on-screen joystick for looking around"
// },
// enableGyro: {
//   id: "preferences-screen.preference.enable-gyro",
//   defaultMessage: "Enable gyroscope (when supported by browser/device)"
// },
// invertTouchscreenCameraMove: {
//   id: "preferences-screen.preference.invert-touchscreen-camera-move",
//   defaultMessage: "Invert direction of camera movement for touchscreens"
// },

export function EnableOnScreenJoystickLeftSwitch({ enableOnScreenJoystickLeft, onEnableOnScreenJoystickLeftChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.enable-on-screen-joystick-left"
      labelDefaultMessage="Enable left on-screen joystick for moving around"
      checked={enableOnScreenJoystickLeft}
      onChange={onEnableOnScreenJoystickLeftChange}
      id="enable-on-screen-joystick-left-switch"
    />
  );
}

EnableOnScreenJoystickLeftSwitch.propTypes = {
  enableOnScreenJoystickLeft: PropTypes.bool.isRequired,
  onEnableOnScreenJoystickLeftChange: PropTypes.func.isRequired
};

export function EnableOnScreenJoystickRightSwitch({
  enableOnScreenJoystickRight,
  onEnableOnScreenJoystickRightChange
}) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.enable-on-screen-joystick-right"
      labelDefaultMessage="Enable right on-screen joystick for looking around"
      checked={enableOnScreenJoystickRight}
      onChange={onEnableOnScreenJoystickRightChange}
      id="enable-on-screen-joystick-right-switch"
    />
  );
}

EnableOnScreenJoystickRightSwitch.propTypes = {
  enableOnScreenJoystickRight: PropTypes.bool.isRequired,
  onEnableOnScreenJoystickRightChange: PropTypes.func.isRequired
};

export function EnableGyroSwitch({ enableGyro, onEnableGyroChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.enable-gyro"
      labelDefaultMessage="Enable gyroscope (when supported by browser/device)"
      checked={enableGyro}
      onChange={onEnableGyroChange}
      id="enable-gyro-switch"
    />
  );
}

EnableGyroSwitch.propTypes = {
  enableGyro: PropTypes.bool.isRequired,
  onEnableGyroChange: PropTypes.func.isRequired
};

export function InvertTouchscreenCameraMoveSwitch({
  invertTouchscreenCameraMove,
  onInvertTouchscreenCameraMoveChange
}) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.invert-touchscreen-camera-move"
      labelDefaultMessage="Invert direction of camera movement for touchscreens"
      checked={invertTouchscreenCameraMove}
      onChange={onInvertTouchscreenCameraMoveChange}
      id="invert-touchscreen-camera-move-switch"
    />
  );
}

InvertTouchscreenCameraMoveSwitch.propTypes = {
  invertTouchscreenCameraMove: PropTypes.bool.isRequired,
  onInvertTouchscreenCameraMoveChange: PropTypes.func.isRequired
};

export function AllowMultipleHubsInstancesSwitch({ allowMultipleHubsInstances, onAllowMultipleHubsInstancesChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.allow-multiple-hubs-instances"
      labelDefaultMessage="Disable auto-exit when multiple room instances are open"
      checked={allowMultipleHubsInstances}
      onChange={onAllowMultipleHubsInstancesChange}
      id="allow-multiple-hubs-instances-switch"
    />
  );
}

AllowMultipleHubsInstancesSwitch.propTypes = {
  allowMultipleHubsInstances: PropTypes.bool.isRequired,
  onAllowMultipleHubsInstancesChange: PropTypes.func.isRequired
};

export function DisableIdleDetectionSwitch({ disableIdleDetection, onDisableIdleDetectionChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.disable-idle-detection"
      labelDefaultMessage="Disable auto-exit when idle or backgrounded"
      checked={disableIdleDetection}
      onChange={onDisableIdleDetectionChange}
      id="disable-idle-detection-switch"
    />
  );
}

DisableIdleDetectionSwitch.propTypes = {
  disableIdleDetection: PropTypes.bool,
  onDisableIdleDetectionChange: PropTypes.func.isRequired
};

export function LazyLoadSceneMediaSwitch({ lazyLoadSceneMedia, onLazyLoadSceneMediaChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.lazy-load-scene-media"
      labelDefaultMessage="Enable Verse media lazy loading"
      checked={lazyLoadSceneMedia}
      onChange={onLazyLoadSceneMediaChange}
      id="lazy-load-scene-media-switch"
    />
  );
}

LazyLoadSceneMediaSwitch.propTypes = {
  lazyLoadSceneMedia: PropTypes.bool.isRequired,
  onLazyLoadSceneMediaChange: PropTypes.func.isRequired
};

export function PreferMobileObjectInfoPanelSwitch({
  preferMobileObjectInfoPanel,
  onPreferMobileObjectInfoPanelChange
}) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.prefer-mobile-object-info-panel"
      labelDefaultMessage="Prefer Mobile Object Info Panel"
      checked={preferMobileObjectInfoPanel}
      onChange={onPreferMobileObjectInfoPanelChange}
      id="prefer-mobile-object-info-panel-switch"
    />
  );
}

PreferMobileObjectInfoPanelSwitch.propTypes = {
  preferMobileObjectInfoPanel: PropTypes.bool.isRequired,
  onPreferMobileObjectInfoPanelChange: PropTypes.func.isRequired
};

export function AnimateWaypointTransitionsSwitch({ animateWaypointTransitions, onAnimateWaypointTransitionsChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.animate-waypoint-transitions"
      labelDefaultMessage="Animate waypoint transitions"
      checked={animateWaypointTransitions}
      onChange={onAnimateWaypointTransitionsChange}
      id="animate-waypoint-transitions-switch"
    />
  );
}

AnimateWaypointTransitionsSwitch.propTypes = {
  animateWaypointTransitions: PropTypes.bool.isRequired,
  onAnimateWaypointTransitionsChange: PropTypes.func.isRequired
};

export function ShowFPSCounterSwitch({ showFPSCounter, onShowFPSCounterChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.show-fps-counter"
      labelDefaultMessage="Show FPS Counter"
      checked={showFPSCounter}
      onChange={onShowFPSCounterChange}
      id="show-fps-counter-switch"
    />
  );
}

ShowFPSCounterSwitch.propTypes = {
  showFPSCounter: PropTypes.bool.isRequired,
  onShowFPSCounterChange: PropTypes.func.isRequired
};

export function ShowRTCDebugPanelSwitch({ showRTCDebugPanel, onShowRTCDebugPanelChange }) {
  return (
    <LabeledSwitch
      labelId="preferences-screen.preference.show-rtc-debug-panel"
      labelDefaultMessage="Show RTC Panel"
      checked={showRTCDebugPanel}
      onChange={onShowRTCDebugPanelChange}
      id="show-rtc-debug-panel-switch"
    />
  );
}

ShowRTCDebugPanelSwitch.propTypes = {
  showRTCDebugPanel: PropTypes.bool.isRequired,
  onShowRTCDebugPanelChange: PropTypes.func.isRequired
};

export function VoiceChatEnabledSwitch({ voiceChatEnabled, onVoiceChatEnabledChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.voice-chat"
      labelDefaultMessage="Voice chat"
      checked={voiceChatEnabled}
      onChange={onVoiceChatEnabledChange}
      id="voice-chat-enabled-switch"
    />
  );
}

VoiceChatEnabledSwitch.propTypes = {
  voiceChatEnabled: PropTypes.bool.isRequired,
  onVoiceChatEnabledChange: PropTypes.func.isRequired
};

// Create switches for: TextChat, Create and move objects, Create Cameras, Pin Objects, Create Drawings, Create Emojis, Allow flyinh
export function TextChatEnabledSwitch({ textChatEnabled, onTextChatEnabledChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.text-chat"
      labelDefaultMessage="Text chat"
      checked={textChatEnabled}
      onChange={onTextChatEnabledChange}
      id="text-chat-enabled-switch"
    />
  );
}

TextChatEnabledSwitch.propTypes = {
  textChatEnabled: PropTypes.bool.isRequired,
  onTextChatEnabledChange: PropTypes.func.isRequired
};

export function CreateAndMoveObjectsEnabledSwitch({
  createAndMoveObjectsEnabled,
  onCreateAndMoveObjectsEnabledChange
}) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.create-and-move-objects"
      labelDefaultMessage="Create and move objects"
      checked={createAndMoveObjectsEnabled}
      onChange={onCreateAndMoveObjectsEnabledChange}
      id="create-and-move-objects-enabled-switch"
    />
  );
}

CreateAndMoveObjectsEnabledSwitch.propTypes = {
  createAndMoveObjectsEnabled: PropTypes.bool.isRequired,
  onCreateAndMoveObjectsEnabledChange: PropTypes.func.isRequired
};

export function CreateCamerasEnabledSwitch({ createCamerasEnabled, onCreateCamerasEnabledChange, disabled }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.create-cameras"
      labelDefaultMessage="Create cameras"
      checked={createCamerasEnabled}
      onChange={onCreateCamerasEnabledChange}
      id="create-cameras-enabled-switch"
      disabled={disabled}
    />
  );
}

CreateCamerasEnabledSwitch.propTypes = {
  createCamerasEnabled: PropTypes.bool.isRequired,
  onCreateCamerasEnabledChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export function PinObjectsEnabledSwitch({ pinObjectsEnabled, onPinObjectsEnabledChange, disabled }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.pin-objects"
      labelDefaultMessage="Pin objects"
      checked={pinObjectsEnabled}
      onChange={onPinObjectsEnabledChange}
      id="pin-objects-enabled-switch"
      disabled={disabled}
    />
  );
}

PinObjectsEnabledSwitch.propTypes = {
  pinObjectsEnabled: PropTypes.bool.isRequired,
  onPinObjectsEnabledChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

export function CreateDrawingsEnabledSwitch({ createDrawingsEnabled, onCreateDrawingsEnabledChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.create-drawings"
      labelDefaultMessage="Create drawings"
      checked={createDrawingsEnabled}
      onChange={onCreateDrawingsEnabledChange}
      id="create-drawings-enabled-switch"
    />
  );
}

CreateDrawingsEnabledSwitch.propTypes = {
  createDrawingsEnabled: PropTypes.bool.isRequired,
  onCreateDrawingsEnabledChange: PropTypes.func.isRequired
};

export function CreateEmojisEnabledSwitch({ createEmojisEnabled, onCreateEmojisEnabledChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.create-emoji"
      labelDefaultMessage="Create emoji"
      checked={createEmojisEnabled}
      onChange={onCreateEmojisEnabledChange}
      id="create-emoji-enabled-switch"
    />
  );
}

CreateEmojisEnabledSwitch.propTypes = {
  createEmojisEnabled: PropTypes.bool.isRequired,
  onCreateEmojisEnabledChange: PropTypes.func.isRequired
};

export function AllowFlyingEnabledSwitch({ allowFlyingEnabled, onAllowFlyingEnabledChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.allow-flying"
      labelDefaultMessage="Allow flying"
      checked={allowFlyingEnabled}
      onChange={onAllowFlyingEnabledChange}
      id="allow-flying-enabled-switch"
    />
  );
}

AllowFlyingEnabledSwitch.propTypes = {
  allowFlyingEnabled: PropTypes.bool.isRequired,
  onAllowFlyingEnabledChange: PropTypes.func.isRequired
};

export function Is360TourEnabledSwitch({ is360TourEnabled, onIs360TourEnabledChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.scene-type-label"
      labelDefaultMessage="Enable 360 Tour"
      checked={is360TourEnabled}
      onChange={onIs360TourEnabledChange}
      id="is-360-tour-enabled-switch"
    />
  );
}

Is360TourEnabledSwitch.propTypes = {
  is360TourEnabled: PropTypes.bool.isRequired,
  onIs360TourEnabledChange: PropTypes.func.isRequired
};

export function TourPortalsEnabledSwitch({ isTourPortalsEnabled, onTourPortalsEnabledChange }) {
  return (
    <LabeledSwitch
      labelId="room-settings-sidebar.enable-tour-portals"
      labelDefaultMessage="Enable Tour Portals"
      checked={isTourPortalsEnabled}
      onChange={onTourPortalsEnabledChange}
      id="is-tour-portals-enabled-switch"
    />
  );
}

TourPortalsEnabledSwitch.propTypes = {
  isTourPortalsEnabled: PropTypes.bool.isRequired,
  onTourPortalsEnabledChange: PropTypes.func.isRequired
};
